/**
 * Hook: useKaratDeductions
 * Fetches and caches karat deductions (dual deductions for Walk-In and Pledge Release)
 */

import { useEffect, useState } from 'react';
import { getAllDeductions } from '../api/karatDeductionApi';

// Cache to avoid repeated API calls
let deductionCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useKaratDeductions = () => {
  const [deductions, setDeductions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDeductions = async (forceFresh = false) => {
    // Check if cache is still valid
    if (!forceFresh && deductionCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setDeductions(deductionCache);
      return deductionCache;
    }

    setLoading(true);
    try {
      const data = await getAllDeductions();
      const deductionMap = {};
      if (Array.isArray(data)) {
        data.forEach((d) => {
          deductionMap[d.purity] = {
            pledgeReleaseDeduction: d.pledgeReleaseDeduction ?? d.deductionPerGram ?? 0,
            walkInDeduction: d.walkInDeduction ?? d.deductionPerGram ?? 0,
            // Keep legacy field for backward compatibility
            deductionPerGram: d.deductionPerGram || 0,
            karat: d.karat,
            purity: d.purity,
          };
        });
      }
      deductionCache = deductionMap;
      cacheTimestamp = Date.now();
      setDeductions(deductionMap);
      setError(null);
      return deductionMap;
    } catch (err) {
      console.error('Error loading karat deductions:', err);
      setError(err);
      // Return empty map on error to avoid breaking forms
      return {};
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeductions();
  }, []);

  /**
   * Calculate display rate: liveRate - deduction
   * @param {number} liveRate - Rate from API
   * @param {string} purity - Purity code (e.g., 'Gold 916')
   * @param {string} caseType - Case type: 'pledge' or 'walkIn'
   * @returns {number} Display rate after deduction
   */
  const getDisplayRate = (liveRate, purity, caseType = 'pledge') => {
    if (!deductions || !purity) return liveRate;
    const deductionObj = deductions[purity];
    if (!deductionObj) return liveRate;
    
    const deduction = caseType === 'walkIn' 
      ? deductionObj.walkInDeduction 
      : deductionObj.pledgeReleaseDeduction;
    
    return Math.max(0, liveRate - deduction);
  };

  /**
   * Get deduction value for a specific case type
   * @param {string} purity - Purity code
   * @param {string} caseType - 'pledge' or 'walkIn'
   * @returns {number} Deduction value
   */
  const getDeductionByType = (purity, caseType = 'pledge') => {
    if (!deductions || !purity) return 0;
    const deductionObj = deductions[purity];
    if (!deductionObj) return 0;
    
    return caseType === 'walkIn' 
      ? deductionObj.walkInDeduction 
      : deductionObj.pledgeReleaseDeduction;
  };

  /**
   * Display rate for one row of the /gold-rates response.
   * Prefers the server-computed netRates (deduction applied in the same response,
   * so it can never be missed because deductions were still loading or failed);
   * falls back to liveRate - deduction for older API responses.
   * @param {object} rateObj - { purity, perGram, netRates? }
   * @param {string} caseType - 'pledge' or 'walkIn'
   */
  const getChannelRate = (rateObj, caseType = 'pledge') => {
    if (!rateObj) return 0;
    const key = caseType === 'walkIn' ? 'walkIn' : 'pledgeRelease';
    const net = rateObj.netRates?.[key]?.perGram;
    if (typeof net === 'number') return net;
    return getDisplayRate(rateObj.perGram || 0, rateObj.purity, caseType);
  };

  return {
    deductions,
    loading,
    error,
    loadDeductions,
    getDisplayRate,
    getChannelRate,
    getDeductionByType,
  };
};
